import {PathLocationStrategy} from '@angular/common';
import {catchError} from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import {lastValueFrom, Observable, Subject} from 'rxjs';
import {BaseService} from './base.service';
import {HttpClient} from './http.service';
import {Logger} from '../logging/default-log.service';
import {FilterState} from '../classes/filter.state';
import {Product} from '../classes/product';
import {LruCache} from '../classes/lru.cache';
import {ProductDetail} from '../classes/product.detail';
import {FilterService} from './filter.service';
import {ProductPage} from '../classes/product.page';
import {ProductsAndFeatures} from '../classes/products.and.features';
import {ConfigurationHelper} from '../helper/configuration.helper';
import {ProductTypes} from '../classes/product.types.enum';
import {PlantscapingService} from './plantscaping.service';
import {WindowRef} from './window-ref.service';

/**
 * Contains the calls to the backend and some business logic for determining the prices.
 *
 */
@Injectable()
export class ProductService extends BaseService {
	private http = inject(HttpClient);
	private filterService = inject(FilterService);
	private locationStrategy = inject(PathLocationStrategy);
	private plantscapingService = inject(PlantscapingService);
	protected logger: Logger;

	public TYPE_STOCK: ProductTypes = ProductTypes.STOCK;
	private productsUrl = ConfigurationHelper.getWebshopUrl('/public/rest/products-and-features');
	private detailUrl = ConfigurationHelper.getWebshopUrl('/public/rest/products/detail');
	private alternativesUrl = ConfigurationHelper.getWebshopUrl('/public/rest/products/alternatives');
	private static winRef: WindowRef = new WindowRef();

	private productAddedToCart = new Subject<Product>();

	productAdded$ = this.productAddedToCart.asObservable();

	/**
	 * Cache of max 1 'products and features' entry,
	 * which is used to make navigating to a product detail page
	 * and back to the overview a lot faster.
	 *
	 * @type {LruCache<Promise<ProductsAndFeatures>>}
	 */
	private productsAndFeaturesResultCache: LruCache<Promise<ProductsAndFeatures>> = new LruCache<
		Promise<ProductsAndFeatures>
	>(1);

	constructor() {
		const logger = inject(Logger);

		super(logger);
	
		this.logger = logger;
	}

	/**
	 * Get alternative products for the given type/pricelistno/pricelistlineno
	 *
	 * @param filterState
	 * @param type
	 * @param priceListNo
	 * @param priceListLineNo
	 */
	getAlternativeProducts(filterState: FilterState, type: number, priceListNo: string, priceListLineNo: string): Promise<Product[]> {
		let url = this.alternativesUrl + `?priceListLineNo=${priceListLineNo}&priceListNo=${priceListNo}`;

		if (filterState) {
			url += '&' + filterState.getParams();
		}

		return lastValueFrom(this.http.get<Product[]>(url));
	}

	/**
	 * Get a single product
	 *
	 * @param filterState
	 * @param type
	 * @param priceListNo
	 * @param priceListLineNo
	 * @returns {Observable<ProductDetail>}
	 */
	getSingle(filterState: FilterState, type: number, priceListNo: string, priceListLineNo: string): Observable<ProductDetail> {
		let url = this.detailUrl + `?type=${type}&priceListLineNo=${priceListLineNo}&priceListNo=${priceListNo}`;

		if (filterState) {
			url += '&' + filterState.getParams();
		}

		if (this.plantscapingService.getPlantscapingFromSession()) {
			url += '&trackPlantscaping=1';
		}

		return this.http
			.get<ProductDetail>(url)
			.pipe(catchError(this.handleError));
	}

	loadProductsAndFeatures(includeFeatures: boolean = true): Promise<ProductsAndFeatures> {
		const filterState = this.filterService.getFilterState();

		return this.getProductsAndFeatures(
			filterState.getParams(),
			includeFeatures
		);
	}

	async getProducts(filtersQueryString: string): Promise<ProductPage> {
		const productsAndFeatures = await this.getProductsAndFeatures(filtersQueryString, false);
		return productsAndFeatures.productPage;
	}

	/**
	 * Returns the products (and optional features).
	 * If the result was already cached (e.g. when the user browses to a product detail and back),
	 * the result is returned from the cache.
	 *
	 * @param {string} filtersQueryString
	 * @param {boolean} includeFeatures
	 * @returns {Promise<ProductsAndFeatures>}
	 */
	getProductsAndFeatures(filtersQueryString: string, includeFeatures: boolean = true): Promise<ProductsAndFeatures> {
		let url = this.productsUrl + '?includefeatures=' + includeFeatures;

		if (filtersQueryString) {
			url += '&' + filtersQueryString;
		}

		const cachedValue = this.productsAndFeaturesResultCache.get(url);

		if (cachedValue) {
			return cachedValue;
		}

		const $promise = lastValueFrom(this.http.get<ProductsAndFeatures>(url));

		// Cache the Promise with the URL as cacheKey.
		this.productsAndFeaturesResultCache.put(url, $promise);

		return $promise;
	}

	/**
	 * Generate an anchor value. Such a value has to start with letter and not a number
	 *
	 * @param type
	 * @param priceListNo
	 * @param priceListLineNo
	 * @returns {string}
	 */
	getAnchorValue(type: number, priceListNo: string, priceListLineNo: string): string {
		return `ID${type}${priceListNo}${priceListLineNo}`;
	}

	/**
	 * Get 'large' image
	 *
	 * @returns {string}
	 */
	getImage(productImage: string): string {
		const src: string[] = productImage.split('.');

		if (!src[0]) {
			return null;
		}

		return src[0];
	}

	/**
	 * Push the location state to the window history with the necessary parameters to build the correct url.
	 *
	 * @param {Product} product
	 * @param {string} baseUrl
	 * @param {string} title (currently not used. use empty string)
	 * @param {string} queryParam
	 */
	pushLocationState(product: Product, baseUrl: string, title: string, queryParam: string): any {
		const fragmentValue = this.getAnchorValue(
			product.type,
			product.priceListNo,
			product.priceListLineNo
		);

		const newStateObject = {productId: fragmentValue};

		const overviewUrl = `${baseUrl}${ProductService.winRef.nativeWindow.location.search}`;

		this.locationStrategy.pushState(
			newStateObject,
			title,
			`${overviewUrl}#${fragmentValue}`,
			queryParam
		);
	}

	/**
	 * Pushes the state specifically for the shops '/products' overview page
	 *
	 * @param product
	 */
	pushProductState(product: Product): any {
		this.pushLocationState(product, '/products', '', '');
	}

	/**
	 * Pushes the state specifically for the website side of things. This can be pushed from places where products are
	 * visible on the site like: hunters or blogs
	 * @param product
	 * @param baseUrl
	 * @param plantHunterId
	 */
	pushWebsiteProductState(product: Product, baseUrl: string, plantHunterId: string) {
		if (plantHunterId) {
			this.pushLocationState(
				product,
				baseUrl + `?hunter=${plantHunterId}`,
				'',
				''
			);
			return;
		}

		this.pushLocationState(product, baseUrl, '', '');
	}

	triggerProductAddedToCartEvent(product: Product) {
		this.productAddedToCart.next(product);
	}

	/**
	 * Triggers the scrollTo event for a product whenever it's called. It requires the anchor value
	 * read from the window.location.hash value.
	 *
	 * @param anchorValue {string}
	 */
	triggerScrollToProductOnWebsite(anchorValue: string): void {
		if (!anchorValue) {
			return;
		}

		const productElement = document.querySelector(anchorValue);

		if (productElement) {
			window.scrollTo(0, productElement.getBoundingClientRect().top);
		}
	}

	getProductDetailUrl(product, barcodeScanner?: boolean, isPlantscaping?: boolean): string {
		let productDetailUrl = `/products/detail;type=${product.type};priceListNo=${product.priceListNo};priceListLineNo=${product.priceListLineNo}`;

		if (product.type === ProductTypes.SOLD_OUT) {
			productDetailUrl = `${productDetailUrl};itemNo=${product.itemNo};potSizeFrom=${product.potSizeFrom};potSizeTill=${product.potSizeTill};potHeightFrom=${product.potHeightFrom};potHeightTill=${product.potHeightTill}`;
		}

		if (barcodeScanner) {
			return `${productDetailUrl};origin=scanner`;
		}

		if (isPlantscaping) {
			return `${productDetailUrl};origin=plantscaping`;
		}

		return productDetailUrl;
	}

	/**
	 * Removes redundant whitespace and trims the resulting text.
	 * @param text
	 */
	trimWhiteSpaceForProductDescriptions(text: string): string {
		return text.replace(/\s+/g, ' ').trim();
	}
}
